<template>
  <div>
    <!-- 使用公共导航栏组件 -->
    <Nav />

    <!-- 主要内容区 -->
    <div class="max-w-7xl mx-auto px-4 pb-8 mt-20">
      <div class="bg-white rounded-xl shadow-sm border border-gray-100">
        <!-- 顶部标题区域 -->
        <div class="border-b border-gray-200 p-6">
          <div class="flex items-center justify-between mb-4">
            <h2 class="text-xl font-bold text-gray-900">我的购物车</h2>
            <button
              class="add-item-btn text-gray-500 hover:text-primary transition-colors cursor-pointer flex items-center"
              @click="showEquipmentSelector = true"
            >
              <div class="w-4 h-4 flex items-center justify-center mr-1">
                <i class="ri-add-line"></i>
              </div>
              <span class="text-sm">添加租赁设备</span>
            </button>
          </div>
        </div>

        <!-- 购物车内容区域 -->
        <div class="p-6">
          <div
            v-if="selectedItems.length === 0"
            class="empty-state text-center text-gray-400 py-16"
          >
            <div
              class="w-20 h-20 flex items-center justify-center mx-auto mb-4"
            >
              <i class="ri-shopping-cart-line text-4xl"></i>
            </div>
            <p class="text-base">您的购物车还是空的</p>
            <button
              class="mt-4 text-primary hover:text-secondary transition-colors cursor-pointer"
              @click="showEquipmentSelector = true"
            >
              添加设备
            </button>
          </div>

          <!-- 按分类分组显示设备 -->
          <div v-else class="selected-items-list">
            <div
              v-for="(group, categoryName) in itemsByCategory"
              :key="categoryName"
              class="mb-8"
            >
              <div class="flex items-center justify-between mb-3">
                <h3 class="font-semibold text-gray-800">
                  分类: {{ categoryName }}
                </h3>
                <button
                  class="text-sm text-gray-500 hover:text-red-500 transition-colors"
                  @click="removeAllByCategory(categoryName)"
                >
                  清空
                </button>
              </div>
              <div class="bg-gray-50 rounded-lg overflow-hidden">
                <div
                  v-for="item in group"
                  :key="item.sku_id"
                  class="flex items-center justify-between p-4 border-b border-gray-100 last:border-b-0"
                >
                  <div class="flex items-center flex-1">
                    <img
                      :src="item.thumbnail_url"
                      :alt="item.name"
                      class="w-16 h-16 object-cover object-top rounded-lg mr-4"
                    />
                    <div class="flex-1">
                      <h4 class="font-medium text-gray-900">
                        {{ item.name }}
                      </h4>
                      <h4 class="text-sm text-gray-500">{{ item.spec_text }}</h4>
                      <p class="text-sm text-gray-500">¥{{ item.price }}/天</p>
                    </div>
                  </div>
                  <div class="flex items-center space-x-4">
                    <div class="flex items-center space-x-2">
                      <button
                        class="quantity-btn decrease w-7 h-7 bg-gray-200 rounded flex items-center justify-center text-gray-600 hover:bg-gray-300 cursor-pointer"
                        @click="updateItemQuantity(item.sku_id, -1)"
                      >
                        <i class="ri-subtract-line text-xs"></i>
                      </button>
                      <span
                        class="quantity text-sm font-medium w-8 text-center"
                        >{{ item.quantity }}</span
                      >
                      <button
                        class="quantity-btn increase w-7 h-7 bg-gray-200 rounded flex items-center justify-center text-gray-600 hover:bg-gray-300 cursor-pointer"
                        @click="updateItemQuantity(item.sku_id, 1)"
                      >
                        <i class="ri-add-line text-xs"></i>
                      </button>
                    </div>
                    <span class="text-primary font-semibold whitespace-nowrap"
                      >¥{{ (item.price * item.quantity).toFixed(2) }}</span
                    >
                    <button
                      class="remove-item w-8 h-8 flex items-center justify-center text-gray-400 hover:text-red-500 cursor-pointer"
                      @click="removeItem(item.sku_id)"
                    >
                      <i class="ri-close-line"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 底部总计和提交区域 -->
        <div
          v-if="selectedItems.length > 0"
          class="bg-gray-50 border-t border-gray-200 p-6"
        >
          <div
            class="flex flex-col md:flex-row items-center justify-between gap-4"
          >
            <div class="text-sm text-gray-500">
              共 {{ totalItemsCount }} 件设备
            </div>
            <div class="flex items-center gap-6">
              <div class="text-xl font-bold text-gray-900">总计:</div>
              <div class="text-3xl font-bold text-primary" id="total-amount">
                ¥{{ totalAmount.toFixed(2) }}
              </div>
              <button
                class="submit-booking bg-primary text-white py-3 px-8 !rounded-button hover:bg-secondary transition-colors text-base font-medium whitespace-nowrap cursor-pointer"
                @click="goToOrder"
              >
                去结算
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 租赁设备选择器组件 -->
    <GoodsSelector
      :visible="showEquipmentSelector"
      :on-close="handleCloseEquipmentSelector"
      :on-add-item="handleAddItem"
    />

    <!-- 使用公共页脚组件 -->
    <Footer />
  </div>
</template>

<script>
import Nav from "@/components/Nav.vue";
import Footer from "@/components/Footer.vue";
import GoodsSelector from "@/components/GoodsSelector.vue";
import api from "@/utils/api";

export default {
  name: "Cart",
  components: {
    Nav,
    Footer,
    GoodsSelector,
  },
  data() {
    return {
      showEquipmentSelector: false,
      selectedItems: [],
    };
  },
  computed: {
    // 按分类分组的设备
    itemsByCategory() {
      const groups = {};
      this.selectedItems.forEach((item) => {
        // 获取分类名称，如果没有则使用"未分类"
        const categoryName = item.category_name || "未分类";
        if (!groups[categoryName]) {
          groups[categoryName] = [];
        }
        groups[categoryName].push(item);
      });
      return groups;
    },

    // 计算购物车中的设备总数
    totalItemsCount() {
      return this.selectedItems.reduce((total, item) => {
        return total + item.quantity;
      }, 0);
    },

    // 计算购物车总金额
    totalAmount() {
      return this.selectedItems.reduce((total, item) => {
        return total + item.price * item.quantity;
      }, 0);
    },
  },
  methods: {
    // 加载购物车数据
    async loadCartData() {
      try {
        const cartData = await api.goods.getCart();
        this.selectedItems = cartData.data || [];
      } catch (error) {
        console.error("加载购物车数据失败:", error);
      }
    },

    // 保存购物车数据到后端
    async saveCartData() {
      try {
        const cartData = {
          selectedItems: this.selectedItems,
          totalAmount: this.totalAmount,
        };
        const res = await api.goods.sendCart(cartData);
      } catch (error) {
        console.error("保存购物车数据失败:", error);
      }
    },

    // 关闭设备选择器
    handleCloseEquipmentSelector() {
      this.showEquipmentSelector = false;
    },

    // 添加设备到已选列表
    handleAddItem(item) {
      const existingItem = this.selectedItems.find(
        (i) => i.sku_id === item.sku_id
      );

      if (existingItem) {
        existingItem.quantity += 1;
        this.$message.success(
          `已将${item.name}数量增加到${existingItem.quantity}`
        );
      } else {
        // 保存设备信息，包括分类信息
        const itemWithInfo = {
          ...item,
          quantity: item.quantity || 1,
          supplier_name: item.supplier_name,
          supplier_id: item.supplier_id,
          category_name: item.category_name,
        };
        this.selectedItems.push(itemWithInfo);
        this.$message.success(`已将${item.name}加入购物车`);
      }

      // 保存购物车数据
      this.saveCartData();
      this.showEquipmentSelector = false;
    },

    // 更新设备数量
    updateItemQuantity(skuId, change) {
      const itemIndex = this.selectedItems.findIndex(
        (item) => item.sku_id === skuId
      );
      if (itemIndex !== -1) {
        const newQuantity = this.selectedItems[itemIndex].quantity + change;
        if (newQuantity > 0) {
          this.selectedItems[itemIndex].quantity = newQuantity;
          // 保存购物车数据
          this.saveCartData();
        } else {
          this.removeItem(skuId);
        }
      }
    },

    // 移除设备
    removeItem(skuId) {
      const itemIndex = this.selectedItems.findIndex(
        (item) => item.sku_id === skuId
      );
      if (itemIndex !== -1) {
        const itemName = this.selectedItems[itemIndex].name;
        this.selectedItems.splice(itemIndex, 1);
        this.$message.success(`已从购物车移除${itemName}`);
        // 保存购物车数据
        this.saveCartData();
      }
    },

    // 移除某个分类的所有设备
    removeAllByCategory(categoryName) {
      if (confirm(`确定要清空分类"${categoryName}"的所有设备吗？`)) {
        this.selectedItems = this.selectedItems.filter(
          (item) => item.category_name !== categoryName
        );
        this.$message.success(`已清空分类"${categoryName}"的所有设备`);
        // 保存购物车数据
        this.saveCartData();
      }
    },

    // 去结算
    goToOrder() {
      if (this.selectedItems.length === 0) {
        this.$message.warning("购物车为空，无法结算");
        return;
      }

      // 保存购物车数据并跳转到确认订单页面
      this.saveCartData();
      this.$router.push("/confirm");
    },
  },
  mounted() {
    // 从localStorage获取购物车数据
    this.loadCartData();
  },
};
</script>

<style scoped>
/* 容器样式 */
</style>



// WEBPACK FOOTER //
// src/pages/goods/Cart.vue